/**
 * PRESSURE (doc 08 §Pressure) — the post-victory system, and the Pearl farm.
 *
 * "One system, two jobs": it is the difficulty ladder for players who have
 * beaten a depth, and it is the reason to go back down. Every active tier pays
 * +20% Pearls on everything that depth earns, and the tiers stack up to six.
 *
 * The seam is the same one the rest of the meta uses: Pressure folds into
 * `RunModifiers` at run construction and the sim never learns the word. A tier
 * that cannot be expressed as a modifier is a tier that does not exist.
 */

import pressureJson from '../content/pressure.json' with { type: 'json' };
import type { RunModifiers } from '../sim/types.ts';

export interface PressureTier {
  id: string;
  name: string;
  line: string;
  /** The exact effect, in the player's words — doc 13's "EXACT numbers" rule. */
  effect: string;
  enemyHpMult?: number;
  bossHpMult?: number;
  gimmickRateMult?: number;
  storageMult?: number;
  costMult?: number;
  sellRefundMult?: number;
  autoLureSeconds?: number;
}

export const PRESSURE_TIERS: PressureTier[] = pressureJson.tiers as PressureTier[];
export const MAX_TIERS = pressureJson.maxTiers;
export const PEARL_BONUS_PER_TIER = pressureJson.pearlBonusPerTier;

export function pressureTier(id: string): PressureTier | undefined {
  return PRESSURE_TIERS.find((t) => t.id === id);
}

/** Only ids that still exist, capped at the maximum. Storage is data; trust nothing. */
export function activeTiers(ids: string[]): PressureTier[] {
  const seen = new Set<string>();
  const out: PressureTier[] = [];
  for (const id of ids) {
    if (seen.has(id)) continue;
    const t = pressureTier(id);
    if (!t) continue;
    seen.add(id);
    out.push(t);
    if (out.length >= MAX_TIERS) break;
  }
  return out;
}

/**
 * Fold the active tiers into the modifiers a run is built with. Multipliers
 * multiply (two storage tiers would compound); the auto-lure timer takes the
 * shortest, because two clocks cannot both be the clock.
 */
export function applyPressure(mods: RunModifiers, ids: string[]): RunModifiers {
  const out = { ...mods };
  for (const t of activeTiers(ids)) {
    if (t.enemyHpMult !== undefined) out.enemyHpMult *= t.enemyHpMult;
    if (t.bossHpMult !== undefined) out.bossHpMult *= t.bossHpMult;
    if (t.gimmickRateMult !== undefined) out.gimmickRateMult *= t.gimmickRateMult;
    if (t.storageMult !== undefined) out.storageMult *= t.storageMult;
    if (t.costMult !== undefined) out.costMult *= t.costMult;
    if (t.sellRefundMult !== undefined) out.sellRefundMult *= t.sellRefundMult;
    if (t.autoLureSeconds !== undefined) {
      out.autoLureSeconds =
        out.autoLureSeconds > 0 ? Math.min(out.autoLureSeconds, t.autoLureSeconds) : t.autoLureSeconds;
    }
  }
  return out;
}

/** Doc 08: +20% Pearls per active tier, on everything the depth earns. */
export function pearlMultiplier(ids: string[]): number {
  return 1 + PEARL_BONUS_PER_TIER * activeTiers(ids).length;
}
